import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/format';
import RapportContent from './components/RapportContent';
import { useBranding } from '../../context/BrandingContext';
import { checkPermission, PERMISSIONS } from '../../utils/permissions';

interface UserInfo { id: string; nom: string; role: string; permissions?: string[]; }

type TabType = 'commandes' | 'stocks' | 'personnel';

const STATUTS = [
  { id: 'tous', label: 'Tous les statuts' },
  { id: 'EN_ATTENTE', label: 'En attente' },
  { id: 'EN_COUPE', label: 'En coupe' },
  { id: 'EN_COUTURE', label: 'En couture' },
  { id: 'TERMINE', label: 'Terminé' },
  { id: 'LIVRE', label: 'Livré' },
];

function getDefaultDates() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { debut: fmt(firstDay), fin: fmt(now) };
}

function formatDateLabel(debut: string, fin: string) {
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  const d1 = new Date(debut).toLocaleDateString('fr-FR', opts);
  const d2 = new Date(fin).toLocaleDateString('fr-FR', opts);
  return `${d1} → ${d2}`;
}

export default function Rapports() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const defaults = getDefaultDates();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('commandes');
  const [dateDebut, setDateDebut] = useState(defaults.debut);
  const [dateFin, setDateFin] = useState(defaults.fin);
  const [statutFilter, setStatutFilter] = useState('tous');
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [pendingExport, setPendingExport] = useState<{ headers: string[]; rows: string[][]; title: string } | null>(null);
  
  // Stats state
  const [stats, setStats] = useState({
    commandes_count: 0,
    total_ca: 0,
    total_encaisse: 0,
    total_dette: 0,
    stock_val: 0,
    alertes_count: 0
  });

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored && token) {
      setUser(JSON.parse(stored));
      api.getGlobalStats(token).then(setStats).catch(console.error);
    }
    else navigate('/login');
  }, [navigate]);

  const hasAccessGlobal = checkPermission(PERMISSIONS.RAPPORT_CA) || checkPermission(PERMISSIONS.CA_VOIR);
  const hasAccessStocks = checkPermission(PERMISSIONS.RAPPORT_STOCKS) || hasAccessGlobal;
  const hasAccessPerso = checkPermission(PERMISSIONS.RAPPORT_PERSONNEL) || hasAccessGlobal;

  useEffect(() => {
    if (!hasAccessGlobal) {
      if (hasAccessStocks) setActiveTab('stocks');
      else if (hasAccessPerso) setActiveTab('personnel');
    }
  }, [hasAccessGlobal, hasAccessStocks, hasAccessPerso]);

  const showToast = (msg: string) => {
    setToast({ message: msg, type: 'success' });
    setTimeout(() => setToast(null), 3000);
  };

  const { totalCA, totalEnc, totalStockVal, alertes, commandesCount } = {
    totalCA: stats.total_ca,
    totalEnc: stats.total_encaisse,
    totalStockVal: stats.stock_val,
    alertes: stats.alertes_count,
    commandesCount: stats.commandes_count
  };
  const totalPersonnelGains = 0; // Will be implemented in Phase 3

  const periodLabel = formatDateLabel(dateDebut, dateFin);

  const handleExcelExport = (headers: string[], rows: string[][], filename: string) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rapport');
    XLSX.writeFile(wb, `${filename}-${dateDebut}-${dateFin}.xlsx`);
    showToast('Export Excel téléchargé');
  };

  const handlePDFExport = (headers: string[], rows: string[][], filename: string) => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(`${branding.nom} — Rapport ` + activeTab.charAt(0).toUpperCase() + activeTab.slice(1), 14, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Période : ${periodLabel} · Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, 21);
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 27,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [46, 90, 63], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 248] },
    });
    doc.save(`${filename}-${dateDebut}-${dateFin}.pdf`);
    showToast('Export PDF téléchargé');
  };

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0 gap-4 flex-wrap">
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Rapports &amp; Analyses</h2>
            <p className="text-xs text-gray-400 mt-0.5">Filtrez par intervalle de dates exact</p>
          </div>

          {/* Date range picker */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
            <div className="w-4 h-4 flex items-center justify-center text-gray-400 flex-shrink-0">
              <i className="ri-calendar-line text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-400 font-medium leading-none mb-1">Du</span>
                <input
                  id="rapport-date-debut"
                  type="date"
                  value={dateDebut}
                  max={dateFin}
                  onChange={e => setDateDebut(e.target.value)}
                  title="Date de début du rapport"
                  className="text-sm font-semibold text-gray-800 bg-transparent border-none outline-none cursor-pointer w-[130px]"
                />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-gray-300 text-lg leading-none">→</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-400 font-medium leading-none mb-1">Au</span>
                <input
                  id="rapport-date-fin"
                  type="date"
                  value={dateFin}
                  min={dateDebut}
                  onChange={e => setDateFin(e.target.value)}
                  title="Date de fin du rapport"
                  className="text-sm font-semibold text-gray-800 bg-transparent border-none outline-none cursor-pointer w-[130px]"
                />
              </div>
            </div>
            <button
              onClick={() => { setDateDebut(defaults.debut); setDateFin(defaults.fin); }}
              title="Réinitialiser au mois en cours"
              className="ml-2 w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line text-xs" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {/* Global KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
            {[
              { label: 'Commandes totales', value: commandesCount, icon: 'ri-file-list-3-line', color: 'bg-white border-gray-100 text-gray-800', visible: hasAccessGlobal },
              { label: "Chiffre d'affaires", value: formatPrice(totalCA), icon: 'ri-money-franc-circle-line', color: 'bg-foret-50 border-foret-200 text-foret-800', visible: hasAccessGlobal },
              { label: 'Encaissé', value: formatPrice(totalEnc), icon: 'ri-wallet-3-line', color: 'bg-green-50 border-green-200 text-green-800', visible: hasAccessGlobal },
              { label: 'Impayés (Dette globale)', value: formatPrice(stats.total_dette || 0), icon: 'ri-error-warning-line', color: (stats.total_dette || 0) > 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800', visible: hasAccessGlobal },
              { label: 'Valeur stocks', value: formatPrice(totalStockVal), icon: 'ri-stack-line', color: alertes > 0 ? 'bg-orange-50 border-orange-200 text-orange-800' : 'bg-white border-gray-100 text-gray-800', visible: hasAccessStocks },
              { label: 'Paie versée / mois', value: formatPrice(totalPersonnelGains), icon: 'ri-team-line', color: 'bg-or-50 border-or-200 text-or-800', visible: hasAccessPerso },
            ].filter(s => s.visible).map(s => (
              <div key={s.label} className={`rounded-xl border px-4 py-3 ${s.color}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-4 h-4 flex items-center justify-center"><i className={s.icon} /></div>
                  <span className="text-xs font-medium opacity-70 truncate">{s.label}</span>
                </div>
                <p className="text-xl font-bold">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Tab + filters bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5 items-start sm:items-center">
            <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
              {([
                { id: 'commandes', label: 'Commandes', icon: 'ri-file-list-3-line', visible: hasAccessGlobal },
                { id: 'stocks', label: 'Stocks', icon: 'ri-stack-line', visible: hasAccessStocks },
                { id: 'personnel', label: 'Personnel', icon: 'ri-team-line', visible: hasAccessPerso },
              ] as const).filter(t => t.visible).map(t => (
                <button
                  key={t.id}
                  onClick={() => { setActiveTab(t.id); setStatutFilter('tous'); setSearchQuery(''); }}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === t.id ? 'bg-foret-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center"><i className={t.icon} /></div>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="relative flex-1 max-w-xs">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line" />
              </div>
              <input
                id="rapport-search-input"
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher…"
                title="Rechercher dans les rapports"
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400"
              />
            </div>

            {activeTab === 'commandes' && (
              <select
                id="rapport-statut-filter"
                value={statutFilter}
                onChange={e => setStatutFilter(e.target.value)}
                title="Filtrer par statut"
                className="border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-foret-400 cursor-pointer"
              >
                {STATUTS.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            )}

            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => { if (pendingExport) handleExcelExport(pendingExport.headers, pendingExport.rows, pendingExport.title); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-semibold rounded-xl cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-excel-2-line text-green-600" /></div>
                Excel
              </button>
              <button
                onClick={() => { if (pendingExport) handlePDFExport(pendingExport.headers, pendingExport.rows, pendingExport.title); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-semibold rounded-xl cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-2-line text-red-500" /></div>
                PDF
              </button>
            </div>
          </div>

          {/* Content */}
          <RapportContent
            activeTab={activeTab}
            statutFilter={statutFilter}
            searchQuery={searchQuery}
            dateDebut={dateDebut}
            dateFin={dateFin}
            onExportData={(headers, rows, title) => {
              setPendingExport({ headers, rows, title });
            }}
          />
        </main>
      </div>

      {/* Export format chooser modal */}
      {pendingExport && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPendingExport(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair font-bold text-lg text-gray-900 mb-2">Exporter le rapport</h3>
            <p className="text-sm text-gray-500 mb-5">
              {pendingExport.rows.length} ligne(s) · {periodLabel}
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => { handleExcelExport(pendingExport.headers, pendingExport.rows, pendingExport.title); setPendingExport(null); }}
                className="flex flex-col items-center gap-2 py-4 rounded-xl border-2 border-green-200 hover:border-green-400 hover:bg-green-50 cursor-pointer transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl">
                  <i className="ri-file-excel-2-line text-green-600 text-xl" />
                </div>
                <span className="text-sm font-semibold text-gray-800">Excel (.xlsx)</span>
                <span className="text-xs text-gray-400">Tableur éditable</span>
              </button>
              <button
                onClick={() => { handlePDFExport(pendingExport.headers, pendingExport.rows, pendingExport.title); setPendingExport(null); }}
                className="flex flex-col items-center gap-2 py-4 rounded-xl border-2 border-red-200 hover:border-red-400 hover:bg-red-50 cursor-pointer transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl">
                  <i className="ri-file-pdf-2-line text-red-500 text-xl" />
                </div>
                <span className="text-sm font-semibold text-gray-800">PDF</span>
                <span className="text-xs text-gray-400">Document imprimable</span>
              </button>
            </div>
            <button onClick={() => setPendingExport(null)} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer transition-colors whitespace-nowrap">
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
